import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Users, ArrowLeft, MessageSquare, Send, Heart, Crown, Settings, Trash2, BarChart3,
} from "lucide-react";
import { PollCard } from "@/components/PollCard";
import { CreatePollForm } from "@/components/CreatePollForm";
import { toast } from "sonner";

export default function GroupDetailPage() {
  const { id } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("posts");
  const [newPost, setNewPost] = useState("");
  const [postLoading, setPostLoading] = useState(false);
  const [polls, setPolls] = useState([]);
  const [showPollForm, setShowPollForm] = useState(false);

  const fetchGroup = useCallback(async () => {
    try {
      const res = await api.get(`/groups/${id}`);
      setGroup(res.data.group);
      setMembers(res.data.members);
    } catch {
      navigate("/groups");
    }
  }, [api, id, navigate]);

  const fetchPosts = useCallback(async () => {
    try {
      const res = await api.get(`/groups/${id}/posts`, { params: { limit: 50 } });
      setPosts(res.data.posts);
    } catch (err) { console.error(err); }
  }, [api, id]);

  const fetchPolls = useCallback(async () => {
    try {
      const res = await api.get("/polls", { params: { group_id: id, limit: 20 } });
      setPolls(res.data.polls);
    } catch (err) { console.error(err); }
  }, [api, id]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchGroup(), fetchPosts(), fetchPolls()]).finally(() => setLoading(false));
  }, [fetchGroup, fetchPosts, fetchPolls]);

  const handleJoin = async () => {
    try {
      const res = await api.post(`/groups/${id}/join`);
      setGroup((g) => ({ ...g, is_member: res.data.joined, member_count: res.data.member_count }));
      if (res.data.joined) fetchGroup();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handlePost = async () => {
    if (!newPost.trim()) return;
    setPostLoading(true);
    try {
      const res = await api.post(`/groups/${id}/posts`, { content: newPost });
      setPosts((prev) => [res.data.post, ...prev]);
      setNewPost("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setPostLoading(false);
  };

  const handleLikePost = async (postId) => {
    try {
      const res = await api.post(`/groups/${id}/posts/${postId}/like`);
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId ? { ...p, liked: res.data.liked, likes_count: res.data.likes_count } : p
        )
      );
    } catch (err) { console.error(err); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Supprimer ce groupe ?")) return;
    try {
      await api.delete(`/groups/${id}`);
      navigate("/groups");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleCreatePoll = async (data) => {
    const res = await api.post("/polls", data);
    setPolls((prev) => [res.data.poll, ...prev]);
    setShowPollForm(false);
  };

  const handleVotePoll = async (pollId, optionId) => {
    const res = await api.post(`/polls/${pollId}/vote`, { option_id: optionId });
    setPolls((prev) => prev.map((p) => (p.id === pollId ? res.data.poll : p)));
    return res.data.poll;
  };

  const handleDeletePoll = async (pollId) => {
    await api.delete(`/polls/${pollId}`);
    setPolls((prev) => prev.filter((p) => p.id !== pollId));
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}j`;
  };

  if (loading) {
    return (
      <div className="py-6 space-y-4">
        <div className="bg-foreground/5 rounded-3xl h-40 animate-pulse" />
        <div className="bg-foreground/5 rounded-3xl h-32 animate-pulse" />
      </div>
    );
  }

  if (!group) return null;

  const isAdmin = group.user_role === "admin";

  return (
    <div className="py-6 space-y-6" data-testid="group-detail-page">
      {/* Back */}
      <button
        onClick={() => navigate("/groups")}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-[#AFA8BA] transition-colors"
        data-testid="group-back-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        Communautes
      </button>

      {/* Group header */}
      <div className="bg-card border border-border rounded-3xl overflow-hidden shadow-sm">
        <div className="h-24 bg-gradient-to-br from-[#7000FF]/40 via-[#FF6B35]/20 to-[#0F0A1F]" />
        <div className="px-5 pb-5 -mt-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#7000FF] to-[#FF8FA3] flex items-center justify-center border-4 border-[#1A1230] shadow-xl">
            <Users className="w-7 h-7 text-white" />
          </div>
          <div className="mt-3 flex items-start justify-between">
            <div>
              <h1 className="font-heading font-extrabold text-2xl text-card-foreground">{group.name}</h1>
              <div className="flex items-center gap-3 mt-1">
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Users className="w-3 h-3" />
                  {group.member_count} membre{group.member_count > 1 ? "s" : ""}
                </span>
                {group.genre && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-card-foreground/80 border border-border">
                    {group.genre}
                  </span>
                )}
                <span className="text-xs text-muted-foreground">
                  par {group.creator_pseudo}
                </span>
              </div>
              {group.description && (
                <p className="text-sm text-card-foreground/80 mt-2 leading-relaxed">{group.description}</p>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              {isAdmin && (
                <button
                  onClick={handleDelete}
                  className="p-2 rounded-xl bg-foreground/5 text-muted-foreground hover:text-red-400 transition-colors"
                  data-testid="group-delete-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          {!group.is_member && (
            <button
              onClick={handleJoin}
              className="mt-4 bg-[#FF6B35] text-white font-bold px-8 py-2.5 rounded-full text-sm hover:scale-105 active:scale-95 transition-transform shadow-[0_0_20px_rgba(255,107,53,0.3)]"
              data-testid="group-join-btn"
            >
              Rejoindre le groupe
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted rounded-full p-1 border border-border">
        <button
          onClick={() => setTab("posts")}
          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
            tab === "posts" ? "bg-[#FF6B35] text-white" : "text-muted-foreground"
          }`}
          data-testid="group-tab-posts"
        >
          <MessageSquare className="w-4 h-4" />
          Discussion
        </button>
        <button
          onClick={() => setTab("polls")}
          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
            tab === "polls" ? "bg-[#FF6B35] text-white" : "text-muted-foreground"
          }`}
          data-testid="group-tab-polls"
        >
          <BarChart3 className="w-4 h-4" />
          Sondages ({polls.length})
        </button>
        <button
          onClick={() => setTab("members")}
          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
            tab === "members" ? "bg-[#FF6B35] text-white" : "text-muted-foreground"
          }`}
          data-testid="group-tab-members"
        >
          <Users className="w-4 h-4" />
          Membres ({group.member_count})
        </button>
      </div>

      {tab === "posts" ? (
        <div className="space-y-4">
          {/* Post creation */}
          {group.is_member && (
            <div className="bg-card border border-border rounded-3xl p-4" data-testid="group-post-form">
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center shrink-0">
                  <span className="text-white text-xs font-bold">
                    {(user?.pseudo || "?")[0].toUpperCase()}
                  </span>
                </div>
                <div className="flex-1">
                  <textarea
                    placeholder="Ecris quelque chose..."
                    value={newPost}
                    onChange={(e) => setNewPost(e.target.value)}
                    className="w-full bg-white border border-border rounded-xl px-3 py-2 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] outline-none resize-none h-16"
                    data-testid="group-post-input"
                  />
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={handlePost}
                      disabled={postLoading || !newPost.trim()}
                      className="bg-[#FF6B35] text-white font-bold px-4 py-1.5 rounded-full text-xs hover:scale-105 active:scale-95 transition-transform disabled:opacity-50 flex items-center gap-1.5"
                      data-testid="group-post-submit"
                    >
                      <Send className="w-3 h-3" />
                      {postLoading ? "..." : "Envoyer"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Posts */}
          {posts.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Aucune discussion</p>
              <p className="text-xs text-muted-foreground mt-1">Sois le premier a lancer la conversation !</p>
            </div>
          ) : (
            posts.map((p) => (
              <div
                key={p.id}
                className="bg-card border border-border rounded-2xl p-4"
                data-testid={`group-post-${p.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center shrink-0">
                    {p.user_avatar ? (
                      <img src={p.user_avatar} alt="" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <span className="text-white text-xs font-bold">
                        {(p.user_pseudo || "?")[0].toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-heading font-semibold text-sm text-card-foreground">{p.user_pseudo}</span>
                      <span className="text-[10px] text-muted-foreground">{timeAgo(p.created_at)}</span>
                    </div>
                    <p className="text-sm text-card-foreground/80 mt-1 leading-relaxed whitespace-pre-wrap">{p.content}</p>
                    <button
                      onClick={() => handleLikePost(p.id)}
                      className="flex items-center gap-1 mt-2 text-xs text-muted-foreground hover:text-[#FF8FA3] transition-colors"
                      data-testid={`group-post-like-${p.id}`}
                    >
                      <Heart className="w-3.5 h-3.5" fill={p.liked ? "currentColor" : "none"} />
                      <span className="font-mono-credits">{p.likes_count || 0}</span>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      ) : tab === "polls" ? (
        <div className="space-y-4">
          {/* Poll creation */}
          {group.is_member && !showPollForm && (
            <button
              onClick={() => setShowPollForm(true)}
              className="w-full bg-card border border-dashed border-[#7000FF]/30 rounded-3xl p-4 flex items-center justify-center gap-2 text-sm text-[#7000FF] hover:border-[#7000FF]/60 hover:bg-[#7000FF]/5 transition-all"
              data-testid="group-create-poll-btn"
            >
              <BarChart3 className="w-4 h-4" />
              Creer un sondage
            </button>
          )}
          {showPollForm && (
            <CreatePollForm
              onSubmit={handleCreatePoll}
              groupId={id}
              onCancel={() => setShowPollForm(false)}
            />
          )}
          {polls.length === 0 && !showPollForm ? (
            <div className="text-center py-12">
              <BarChart3 className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Aucun sondage</p>
              <p className="text-xs text-muted-foreground mt-1">Cree un sondage pour engager le groupe !</p>
            </div>
          ) : (
            polls.map((p) => (
              <PollCard
                key={p.id}
                poll={p}
                onVote={handleVotePoll}
                onDelete={p.creator_id === user?.id ? handleDeletePoll : undefined}
              />
            ))
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {members.map((m) => (
            <div
              key={m.id}
              className="bg-card border border-border rounded-2xl p-3 flex items-center gap-3"
              data-testid={`group-member-${m.user_id}`}
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center shrink-0">
                {m.avatar_url ? (
                  <img src={m.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <span className="text-white font-heading font-bold text-sm">
                    {(m.pseudo || "?")[0].toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-sm text-card-foreground truncate">{m.pseudo}</p>
                <p className="text-[10px] text-muted-foreground">
                  Membre depuis {new Date(m.joined_at).toLocaleDateString("fr-FR")}
                </p>
              </div>
              {m.role === "admin" && (
                <span className="flex items-center gap-1 text-[10px] text-[#FF6B35] bg-[#FF6B35]/10 px-2 py-0.5 rounded-full">
                  <Crown className="w-3 h-3" />
                  Admin
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
